import { Modal } from "..";
import ThumbnailSelectionModal from "./ThumbnailSelectionModal";
import styles from "./thumbnailSelection.module.scss";
import { useEffect, useState } from "react";
import { AssetItem, MediaModel } from "@/database";
import "../../scss/theme.scss";
// import noImage from '../../assets/icons/svg/no-image.svg';
import noImageLandscape from "../../assets/icons/svg/no-thumb-landscape.jpg";
import noImagePodcast from "../../assets/icons/svg/no-thumb-podcast.jpg";
import noImagePortrait from "../../assets/icons/svg/no-thumb-portrait.jpg";
import { Image, FileImage, BookImage, Send } from "lucide-react";

import { Button, Tooltip } from "..";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";

interface ImageState {
  url: string;
  id: string;
}

interface ThumbnailSelectionProps {
  baseUrl: string;
  assets: AssetItem[];
  currentThumbnail?: string;
  handleThumbnailChange: (
    newThumbnailUrl: string,
    newThumbnailBase64: string
  ) => void;
  topLabel?: string;
  bottomLabel?: string;
  disabled?: boolean;
  error?: any;
  type?: string;
  currentThumbnailId?: string;
  touched?: any;
  updateModal?: any;
  isWizard?: boolean;
  aspectRatio?: number;
  currentMediaDetails?: MediaModel;
  thumbType?: string;
  isRequiredThumb?: boolean;
}

const ThumbnailSelection = ({
  baseUrl,
  currentThumbnail,
  assets,
  handleThumbnailChange,
  topLabel,
  bottomLabel,
  disabled = false,
  error,
  type,
  currentThumbnailId,
  touched,
  updateModal,
  isWizard,
  aspectRatio,
  currentMediaDetails,
  thumbType,
  isRequiredThumb = false,
}: ThumbnailSelectionProps) => {
  const [modalTitle, setModalTitle] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null
  );
  const [url, setUrl] = useState<string>("");
  const [id, setId] = useState<string>("");
  const [flag, setFlag] = useState<boolean>(false);
  const [wasCropperOpened, setWasCropperOpened] = useState<boolean>(false);
  const [cropFunction, setCropFunction] = useState<(() => void) | null>(null);
  // Store skip handler in an object so setState doesn't invoke it (React treats setState(fn) as updater)
  const [skipCropFunction, setSkipCropFunction] = useState<{
    fn: () => void;
  } | null>(null);
  const [images, setImages] = useState<ImageState[]>([]);

  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string>("");

  const fallbackImage =
    type === "podcast"
      ? noImagePodcast
      : type === "portrait"
        ? noImagePortrait
        : noImageLandscape;

  const getFallbackIconAndText = () => {
    switch (type) {
      case "podcast":
        return { icon: <BookImage size={28} />, text: "" };
      case "portrait":
        return { icon: <FileImage size={28} />, text: "" };
      case "social":
        return { icon: <Send size={28} />, text: "" };
      case "poster":
        return { icon: <Image size={28} />, text: "" };
      default:
        return { icon: <Image size={28} />, text: "" };
    }
  };

  const handleFlag = () => {
    setFlag(!flag);
  };

  const closeModal = () => {
    if (isWizard) {
      updateModal(true);
    }
    setIsModalOpen(false);
    setModalContent(null);
    setModalTitle("");
    setWasCropperOpened(false);
    setCropFunction(null);
    setSkipCropFunction(null);
  };

  useEffect(() => {
    // const separator = baseUrl?.endsWith('/') ? '' : '/';

    const allImages = assets
      .filter((item) => item?.name === "thumb" && item.status === 3)
      .map((item) => {
        const useOriginalImage = type === "podcast" || type === "portrait";
        const imageExtension = useOriginalImage ? ".jpg" : ".jpg"; //as it converts to 16/9 ratio

        return {
          id: item.id,
          url: !item?.baseurl
            ? `${baseUrl}${baseUrl?.endsWith("/") ? "" : "/"}${item?.id}${
                currentMediaDetails?.source === "dbsync"
                  ? ".jpg"
                  : imageExtension
              }`
            : `${item?.baseurl}/${item?.id}${
                currentMediaDetails?.source === "dbsync"
                  ? ".jpg"
                  : imageExtension
              }`,
        };
      });
    setImages(allImages);
  }, [assets, type]);

  const isLocalThumbnail =
    !!currentThumbnail &&
    (currentThumbnail.startsWith("data:") ||
      currentThumbnail.startsWith("blob:"));

  useEffect(() => {
    const useOriginalImage = type === "podcast" || type === "portrait";
    const imageExtension = useOriginalImage ? ".jpg" : ".jpg"; //as it converts to 16/9 ratio
    const thumbURL =
      baseUrl && !isLocalThumbnail
        ? `${baseUrl}${baseUrl?.endsWith("/") ? "" : "/"}${currentThumbnailId}${
            currentMediaDetails?.source === "dbsync" ? ".jpg" : imageExtension
          }`
        : currentThumbnail;
    setCurrentThumbnailUrl(thumbURL || "");
  }, [currentThumbnail, baseUrl, currentThumbnailId, type, isLocalThumbnail]);

  const createAndOpenThumbnailSelectionModal = () => {
    if (disabled) return;
    setWasCropperOpened(false);
    setCropFunction(null);
    setModalTitle(thumbType || "THUMBNAIL");
    setModalContent(
      <ThumbnailSelectionModal
        images={images}
        handleThumbnailSelected={({ url, id }: { url: string; id: string }) => {
          setUrl(url);
          setId(id);
          if (id?.startsWith("cropped-")) {
            setWasCropperOpened(true);
          } else {
            setWasCropperOpened(false);
          }
        }}
        closeModal={closeModal}
        type={type}
        currentThumbnailId={currentThumbnailId}
        handleFlag={handleFlag}
        aspectRatio={aspectRatio}
        onCropRequest={(cropFn: () => void) => {
          setWasCropperOpened(true);
          setCropFunction(() => cropFn);
        }}
        onCropperClose={() => {
          setWasCropperOpened(false);
          setCropFunction(null);
          setSkipCropFunction(null);
        }}
        onCropperSkipReady={(skipFn) => {
          setSkipCropFunction(skipFn ? { fn: skipFn } : null);
        }}
        isWizard={isWizard}
        isRequiredThumb={isRequiredThumb}
      />
    );
    setIsModalOpen(true);
  };

  return (
    <>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          title={modalTitle}
          onClose={closeModal}
          updateModal={updateModal}
          zIndex={1070}
          // height="auto"
          width="60vw"
          // minWidth="60vw"
          footer={
            <div className="d-flex gap-2 justify-content-end">
              {wasCropperOpened && skipCropFunction && (
                <Button
                  type="outline-secondary"
                  onClick={() => {
                    trackEvent("upload", {
                      category: "thumbnail",
                      entity: id || currentThumbnailId || "thumbnail",
                      action: "use original",
                      location: isWizard ? "wizard" : "non-wizard",
                      ...getTrackUserContext(),
                    });
                    skipCropFunction.fn();
                    setWasCropperOpened(false);
                    setSkipCropFunction(null);
                    setCropFunction(null);
                  }}
                >
                  Use Original
                </Button>
              )}
              <Button
                onClick={() => {
                  if (wasCropperOpened && cropFunction) {
                    trackEvent("upload", {
                      category: "thumbnail",
                      entity: id || currentThumbnailId || "thumbnail",
                      action: "apply crop",
                      location: isWizard ? "wizard" : "non-wizard",
                      ...getTrackUserContext(),
                    });
                    cropFunction();
                  } else {
                    setIsModalOpen(false);
                    if (url && id) {
                      handleThumbnailChange(url, id);
                    }
                  }
                }}
              >
                {wasCropperOpened ? "Apply Crop" : "Done"}
              </Button>
            </div>
          }
        >
          {modalContent}
        </Modal>
      )}
      <div className={`${isWizard ? "col-2" : "col-2 p-0"}`}>
        {bottomLabel ? (
          <Tooltip
            content={bottomLabel}
            placement="top"
            delay={{ show: 250, hide: 150 }}
          >
            <div
              style={{ width: "100%" }}
              className={`ratio ratio-16x9 position-relative p-0 bg-white ${
                topLabel || bottomLabel ? "border rounded" : "border"
              }`}
            >
              {topLabel && <div>{topLabel}</div>}
              <div className="d-flex gap-2 bg-light">
                {isLocalThumbnail ||
                (currentThumbnailId &&
                  currentThumbnailId !== "" &&
                  currentThumbnailId !== "undefined") ? (
                  <div
                    style={{
                      height: "100%",
                      maxWidth: "100%",
                      minWidth: "100%",
                      position: "relative",
                      // backgroundColor: 'gray',
                    }}
                    className={`${
                      topLabel || bottomLabel ? "rounded-top" : ""
                    } d-flex justify-content-center align-items-center ${
                      styles["change_div_container"]
                    }`}
                  >
                    <img
                      src={currentThumbnailUrl}
                      height="100%"
                      min-width="100%"
                      max-width="100%"
                      style={{ objectFit: "contain", maxWidth: "100%" }}
                      alt=""
                      className="rounded-top"
                    />
                    <div
                      className={`blackTransparent gap-2 ${styles["change_div"]}`}
                      style={{ zIndex: 2 }}
                      onClick={() => {
                        createAndOpenThumbnailSelectionModal();
                      }}
                    >
                      <Image size={20} />
                      {!disabled && <span className="px-1">CHANGE</span>}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: "100%",
                      maxWidth: "100%",
                      minWidth: "100%",
                      position: "relative",
                    }}
                    className={`${
                      topLabel || bottomLabel ? "rounded-top" : ""
                    } d-flex justify-content-center align-items-center ${
                      styles["change_div_container"]
                    }`}
                  >
                    <div className="fallback-image text-secondary">
                      {(() => {
                        const { icon, text } = getFallbackIconAndText();
                        return (
                          <>
                            {icon}
                            {text}
                          </>
                        );
                      })()}
                    </div>

                    {/* <img
                      src={fallbackImage}
                      height="100%"
                      min-width="100%"
                      max-width="100%"
                      style={{ objectFit: 'contain', maxWidth: '100%' }}
                      alt=""
                      // className="rounded-top"
                    /> */}

                    <div
                      className={`blackTransparent gap-2 ${styles["change_div"]}`}
                      style={{ zIndex: 2 }}
                      onClick={() => {
                        createAndOpenThumbnailSelectionModal();
                      }}
                    >
                      <Image />
                      {!disabled && <span className="px-1">UPLOAD</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tooltip>
        ) : (
          <div
            style={{ width: "160px" }}
            className={`ratio ratio-16x9 position-relative p-0 bg-white ${
              topLabel || bottomLabel ? "rounded" : "border"
            }`}
          >
            {topLabel && <div>{topLabel}</div>}
            <div className="d-flex gap-2 bg-light">
              {currentThumbnailId &&
              currentThumbnailId !== "" &&
              currentThumbnailId !== "undefined" &&
              currentThumbnailId !== "undefined.jpg" ? (
                <div
                  style={{
                    height: "100%",
                    maxWidth: "100%",
                    minWidth: "100%",
                    position: "relative",
                    // backgroundColor: 'gray',
                  }}
                  className={`${
                    topLabel || bottomLabel ? "rounded-top" : ""
                  } d-flex justify-content-center align-items-center ${
                    styles["change_div_container"]
                  }`}
                >
                  <img
                    src={currentThumbnailUrl}
                    height="100%"
                    min-width="100%"
                    max-width="100%"
                    style={{ objectFit: "contain", maxWidth: "100%" }}
                    alt=""
                    className="rounded-top"
                  />

                  <div
                    className={`blackTransparent gap-2 ${styles["change_div"]}`}
                    style={{ zIndex: 2 }}
                    onClick={() => {
                      createAndOpenThumbnailSelectionModal();
                    }}
                  >
                    <i className="bi bi-images text-light"></i>
                    {!disabled && <span className="px-1">CHANGE</span>}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    height: "100%",
                    maxWidth: "100%",
                    minWidth: "100%",
                    position: "relative",
                  }}
                  className={`${
                    topLabel || bottomLabel ? "rounded-top" : ""
                  } d-flex justify-content-center align-items-center ${
                    styles["change_div_container"]
                  }`}
                >
                  <img
                    src={fallbackImage}
                    height="100%"
                    min-width="100%"
                    max-width="100%"
                    style={{
                      objectFit: "contain",
                      maxWidth: "100%",
                      opacity: 0.4,
                    }}
                    alt=""
                    className="rounded-top"
                  />

                  <div
                    className={`blackTransparent gap-2 ${styles["change_div"]}`}
                    style={{ zIndex: 2 }}
                    onClick={() => {
                      createAndOpenThumbnailSelectionModal();
                    }}
                  >
                    <i className="bi bi-images text-light fs-5"></i>
                    {!disabled && <span className="px-1">UPLOAD</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {bottomLabel && <div className="bg-dark-subtle p-2">{bottomLabel}</div>}
        {error && touched && (
          <div className="position-absolute text-danger top-10 mb-3">
            {error}
          </div>
        )}
      </div>
    </>
  );
};

export default ThumbnailSelection;
